import React, {
	type ComponentType,
	type ErrorInfo,
	type ReactNode,
	type Ref,
	type RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react';

import { di } from 'react-magnetic-di';
import { loadEntryPoint, type EntryPointProps as RelayEntryPointProps } from 'react-relay';
import { useMergeRefs } from 'use-callback-ref';

import Modal, { type ModalDialogProps } from '@atlaskit/modal-dialog';
import { useEntryPointConfig } from '@atlassian/entry-point-config';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ModalContext } from '../modal-context-provider/modal-context';

import { FocusInsideOnLoad } from './focus-inside-on-load';
import { Loading } from './loading';
import { type ModalComponentProps } from './types';

export { type ModalComponentProps };

export type ModalEntryPointProps<
	TPreloadedQueries,
	TNestedEntryPoints,
	TRuntimeProps,
	TExtraProps,
> = RelayEntryPointProps<
	TPreloadedQueries,
	TNestedEntryPoints,
	TRuntimeProps & ModalComponentProps,
	TExtraProps
>;

type EntryPointProps<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

export type ModalTriggerProps<TEntryPoint> = {
	children: ({ ref }: { ref: RefObject<HTMLElement> }) => ReactNode;
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<
		EntryPointProps<TEntryPoint>,
		| 'onClose'
		| 'setShouldCloseOnOverlayClick'
		| 'setShouldCloseOnEscapePress'
		| 'shouldCloseOnOverlayClick'
		| 'shouldCloseOnEscapePress'
	> & {
		onClose?: (extraInformation?: unknown) => void;
	};
	Fallback?: ComponentType<{ onClose?: () => void }>;
	forwardedRef?: Ref<HTMLElement>;
	modalProps?: ModalDialogProps;
	onError?: ErrorCallback;
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	preloadOnFocus?: boolean;
	title?: ReactNode;
	/**
	 * The text that renders during the loading screen.
	 */
	loadingText?: ReactNode;
};

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as EntryPointProps<AnyEntryPoint>;

export function ModalTrigger<TEntryPoint extends AnyEntryPoint>({
	children,
	entryPoint,
	entryPointParams,
	entryPointProps,
	Fallback,
	title,
	modalProps,
	forwardedRef,
	onError,
	preloadOnFocus = true,
	loadingText,
}: ModalTriggerProps<TEntryPoint>): React.ReactNode {
	di(loadEntryPoint);
	const { openModal } = useContext(ModalContext);
	const environmentProvider = useRelayEnvironmentProvider();
	const { ModalErrorFallback } = useEntryPointConfig();
	const modalRef = useRef<HTMLElement>(null);

	const metricName = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<unknown>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({
			reference: entryPointReference,
			dispose,
		}: {
			dispose: () => void;
			reference: EntryPointRef<TEntryPoint>;
		}) => {
			openModal(
				({
					onClose: closeModal,
					shouldCloseOnOverlayClick,
					shouldCloseOnEscapePress,
					setShouldCloseOnOverlayClick,
					setShouldCloseOnEscapePress,
				}) => {
					const onClose = (extraInformation?: unknown) => {
						entryPointProps?.onClose?.(extraInformation);
						closeModal();
						dispose();
					};

					const container = (
						<InternalEntryPointContainer
							entryPointReference={entryPointReference}
							errorFallback={({ error }: { error: Error }) => <ModalErrorFallback error={error} />}
							onError={({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo }) => {
								onError?.({ error, errorInfo });
								closeModal();
							}}
							fallback={
								Fallback ? (
									<Fallback onClose={closeModal} />
								) : (
									<Loading title={title} loadingText={loadingText} />
								)
							}
							id={metricName}
							placeholderName="modal-entry-point-pressable-trigger-container"
							runtimeProps={
								{
									...(entryPointProps ?? emptyEntryPointProps),
									onClose,
									setShouldCloseOnOverlayClick,
									setShouldCloseOnEscapePress,
									shouldCloseOnOverlayClick,
									shouldCloseOnEscapePress,
								} as unknown as EntryPointProps<TEntryPoint>
							}
						/>
					);

					return (
						// eslint-disable-next-line @atlaskit/design-system/use-modal-dialog-close-button
						<Modal
							{...modalProps}
							ref={modalRef}
							onClose={onClose}
							shouldCloseOnOverlayClick={
								shouldCloseOnOverlayClick ?? modalProps?.shouldCloseOnOverlayClick
							}
							shouldCloseOnEscapePress={
								shouldCloseOnEscapePress ?? modalProps?.shouldCloseOnEscapePress
							}
						>
							{container}
							<FocusInsideOnLoad
								entryPointReference={entryPointReference}
								autoFocusRef={modalProps?.autoFocus}
								modalRef={modalRef}
							/>
						</Modal>
					);
				},
			);
		},
		[
			openModal,
			title,
			modalProps,
			Fallback,
			metricName,
			entryPointProps,
			ModalErrorFallback,
			onError,
			loadingText,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		preloadOnFocus,
	});

	const mergedRef = useMergeRefs([triggerRef, forwardedRef || null]);

	return children({ ref: mergedRef as unknown as RefObject<HTMLElement> });
}
